/**
 * App Component
 * 
 * Main application component that sets up routing, authentication context,
 * and initializes the database connection. This component serves as the
 * entry point for the SoberLivingSupport application.
 */

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Component imports
import Navbar from './components/Navbar';
import SiteFooter from './components/SiteFooter';

// Context and service imports
import { AuthProvider } from './contexts/AuthContext';

// Styles
import './App.css';

// Lazy-loaded pages for code splitting
const Home = React.lazy(() => import('./pages/Home/Home'));
const Listings = React.lazy(() => import('./pages/Listings/Listings'));
const ListingDetails = React.lazy(() => import('./pages/ListingDetails/ListingDetails'));
const Signup = React.lazy(() => import('./pages/Signup/Signup'));
const SignIn = React.lazy(() => import('./pages/SignIn/SignIn'));
const AccountTypeSelection = React.lazy(() => import('./pages/AccountTypeSelection/AccountTypeSelection'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const UserProfile = React.lazy(() => import('./pages/UserProfile/UserProfile'));
const HomeownerProfile = React.lazy(() => import('./pages/HomeownerProfile/HomeownerProfile'));
const HomeownerListings = React.lazy(() => import('./pages/HomeownerListings/HomeownerListings'));
const HomeownerListingForm = React.lazy(() => import('./pages/HomeownerListingForm/HomeownerListingForm'));
const AdminLogin = React.lazy(() => import('./pages/Admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/Admin/AdminDashboard'));
const ManageUsers = React.lazy(() => import('./pages/Admin/ManageUsers'));
const ManageListings = React.lazy(() => import('./pages/Admin/ManageListings'));
const AdminSettings = React.lazy(() => import('./pages/Admin/AdminSettings'));
const NotFound = React.lazy(() => import('./pages/NotFound/NotFound'));
const InfoPage = React.lazy(() => import('./pages/Info/InfoPage'));

/**
 * Main App component function
 * 
 * Initializes the database connection on component mount and
 * sets up the application routing structure with authentication context.
 * 
 * @returns {JSX.Element} The rendered application
 */
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="flex min-h-screen flex-col">
          <Navbar />
          <main className="flex-1">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-medium mb-4"></div>
                  <p className="text-brand-dark text-lg font-semibold">Loading...</p>
                </div>
              </div>
            }>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/listings" element={<Listings />} />
              <Route path="/listings/:id" element={<ListingDetails />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/choose-account-type" element={<AccountTypeSelection />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/about" element={<InfoPage page="about" />} />
              <Route path="/privacy" element={<InfoPage page="privacy" />} />
              <Route path="/terms" element={<InfoPage page="terms" />} />
              <Route path="/contact" element={<InfoPage page="contact" />} />
              
              {/* Individual User Profile Routes */}
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/profile/saved" element={<UserProfile />} />
              <Route path="/profile/waitlists" element={<UserProfile />} />
              <Route path="/profile/settings" element={<UserProfile />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<ManageUsers />} />
              <Route path="/admin/listings" element={<ManageListings />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              
              {/* Homeowner Routes */}
              <Route path="/homeowner-profile" element={<HomeownerProfile />} />
              <Route path="/homeowner-profile/listings" element={<HomeownerListings />} />
              <Route path="/homeowner-profile/listings/new" element={<HomeownerListingForm />} />
              <Route path="/homeowner-profile/listings/edit/:id" element={<HomeownerListingForm />} />
              <Route path="/homeowner-profile/inquiries" element={<HomeownerProfile />} />
              <Route path="/homeowner-profile/settings" element={<HomeownerProfile />} />

              {/* Catch-all 404 */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <SiteFooter />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
